import React, { useCallback, useState } from 'react';
import { useTranslation } from 'localization';
import type { SphereAttachment } from 'types/attachment';
import { getIconClass } from '../../../utils/attachmentIcon';
import { formatSize } from '../../../utils/formatBytes';
import './AttachmentTileAtom.scss';

const IMAGE_PREVIEW_MIMES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

export interface AttachmentTileAtomProps {
  attachment: SphereAttachment;
  thumbnailUrl?: string;
  canDelete?: boolean;
  onDelete?: () => void;
  onEdit?: () => void;
  onClick?: () => void;
  className?: string;
}

function extractHostname(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

const AttachmentTileAtom: React.FC<AttachmentTileAtomProps> = ({
  attachment,
  thumbnailUrl,
  canDelete = false,
  onDelete,
  onEdit,
  onClick,
  className = '',
}) => {
  const { t } = useTranslation();
  const [thumbnailErrored, setThumbnailErrored] = useState(false);

  const scanStatus = attachment.scan_status;
  const isScanPending = scanStatus === 'pending';
  const isRemoved = scanStatus === 'malicious' || scanStatus === 'quarantined';
  const isLink = attachment.attachment_type === 'link';
  const mime = attachment.mime_type ?? '';
  const isImagePreview =
    !isLink && !isRemoved && IMAGE_PREVIEW_MIMES.has(mime) && !!thumbnailUrl && !thumbnailErrored;

  const isClickable = typeof onClick === 'function' && !isScanPending && !isRemoved;
  const showDelete = canDelete && typeof onDelete === 'function' && !isRemoved;
  const showEdit = typeof onEdit === 'function' && !isScanPending && !isRemoved;

  const displayName = attachment.name?.trim() || t('attachments.unnamed');
  const typeLabel = isLink ? t('attachments.types.link') : t('attachments.types.file');
  const iconClass = getIconClass(attachment);
  const hostname = isLink ? extractHostname(attachment.url) : null;
  const sizeLabel = isLink ? null : formatSize(attachment.size_bytes);
  const meta = isLink ? hostname : sizeLabel;

  let tileTitle: string;
  if (isScanPending) {
    tileTitle = t('attachments.scanPending');
  } else if (isRemoved) {
    tileTitle = t('attachments.removedMalicious');
  } else {
    tileTitle = displayName;
  }

  const handleClick = useCallback(() => {
    if (isClickable) onClick?.();
  }, [isClickable, onClick]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      // <button> natively handles Enter/Space → click, so we only need the
      // Delete/Backspace shortcut here.
      if (showDelete && (event.key === 'Delete' || event.key === 'Backspace')) {
        event.preventDefault();
        onDelete?.();
      }
    },
    [showDelete, onDelete],
  );

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onDelete?.();
    },
    [onDelete],
  );

  const handleEditClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onEdit?.();
    },
    [onEdit],
  );

  const handleThumbnailError = useCallback(() => {
    setThumbnailErrored(true);
  }, []);

  const rootClasses = [
    'attachment-tile-atom',
    isClickable ? 'attachment-tile-atom--clickable' : '',
    isLink ? 'attachment-tile-atom--link' : 'attachment-tile-atom--file',
    isScanPending ? 'attachment-tile-atom--scanning' : '',
    isRemoved ? 'attachment-tile-atom--removed' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={rootClasses}
      data-attachment-id={attachment.id}
      data-scan-status={scanStatus}
    >
      <button
        type="button"
        className="attachment-tile-atom__interactive"
        title={tileTitle}
        onClick={isClickable ? handleClick : undefined}
        onKeyDown={handleKeyDown}
        aria-label={`${typeLabel}: ${displayName}`}
        aria-busy={isScanPending || undefined}
        aria-disabled={isScanPending || isRemoved || undefined}
      >
      <div className="attachment-tile-atom__media" aria-hidden="true">
        {isImagePreview ? (
          <img
            className="attachment-tile-atom__thumbnail"
            src={thumbnailUrl}
            alt=""
            onError={handleThumbnailError}
          />
        ) : (
          <i
            className={`${isLink ? 'pi pi-link' : iconClass} attachment-tile-atom__icon`}
          />
        )}
        {isScanPending && (
          <div
            className="attachment-tile-atom__overlay attachment-tile-atom__overlay--scanning"
            role="status"
            aria-live="polite"
          >
            <i className="pi pi-spin pi-spinner" aria-hidden="true" />
            <span>{t('attachments.scanning')}</span>
          </div>
        )}
        {isRemoved && (
          <div
            className="attachment-tile-atom__overlay attachment-tile-atom__overlay--removed"
            role="status"
            aria-live="polite"
          >
            <i className="pi pi-shield" aria-hidden="true" />
            <span>{t('attachments.removedMalicious')}</span>
          </div>
        )}
      </div>
        <div className="attachment-tile-atom__body">
          <div className="attachment-tile-atom__name" title={displayName}>
            {displayName}
          </div>
          {meta && (
            <div className="attachment-tile-atom__meta" title={meta}>
              {meta}
            </div>
          )}
        </div>
      </button>
      {(showEdit || showDelete) && (
        <div className="attachment-tile-atom__actions">
          {showEdit && (
            <button
              type="button"
              className="attachment-tile-atom__action attachment-tile-atom__action--edit"
              onClick={handleEditClick}
              aria-label={t('attachments.editAriaLabel')}
            >
              <i className="pi pi-pencil" aria-hidden="true" />
            </button>
          )}
          {showDelete && (
            <button
              type="button"
              className="attachment-tile-atom__action attachment-tile-atom__action--delete"
              onClick={handleDeleteClick}
              aria-label={t('attachments.delete')}
            >
              <i className="pi pi-trash" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AttachmentTileAtom;
